// Credit-report pull schedule for one client: how often the system pulls a
// fresh report automatically (default every 14 days, or off) and a "Pull now"
// button that imports + analyzes a fresh report on demand.
import { useState } from "react";
import { ApiError, clients as clientsApi, reports as reportsApi, type Client } from "../lib/api";

const INTERVALS: Array<{ days: number; label: string }> = [
  { days: 0, label: "Off — pull only when I click Pull now" },
  { days: 7, label: "Every 7 days" },
  { days: 14, label: "Every 14 days (recommended)" },
  { days: 30, label: "Every 30 days" },
  { days: 45, label: "Every 45 days" },
  { days: 60, label: "Every 60 days" },
  { days: 90, label: "Every 90 days" },
];

function when(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export default function ReportScheduleCard({
  client, hasMonitoringLogin, onClientUpdated, onPulled,
}: {
  client: Client;
  hasMonitoringLogin: boolean;
  onClientUpdated: (clientId: string, patch: Partial<Client>) => void;
  /** Called with the new report id after a Pull now that saved a report. */
  onPulled: (clientId: string, reportId: string) => void;
}) {
  const interval = client.auto_import_interval_days ?? 14;
  const [saving, setSaving] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function changeInterval(days: number) {
    setSaving(true); setErr(null); setMsg(null);
    try {
      await clientsApi.update(client.id, { auto_import_interval_days: days });
      const next = days > 0 && client.last_report_at
        ? new Date(new Date(client.last_report_at).getTime() + days * 864e5).toISOString()
        : null;
      onClientUpdated(client.id, { auto_import_interval_days: days, next_auto_import_at: next });
      setMsg(days === 0 ? "Automatic pulls turned off." : `Saved — reports pull automatically every ${days} days.`);
    } catch (e: any) {
      setErr(e?.message || "Could not save the schedule.");
    } finally { setSaving(false); }
  }

  function recordPulled(reportId: string | undefined) {
    onClientUpdated(client.id, { last_report_at: new Date().toISOString(),
      next_auto_import_at: interval > 0 ? new Date(Date.now() + interval * 864e5).toISOString() : null });
    if (reportId) onPulled(client.id, reportId);
  }

  async function pullNow() {
    setPulling(true); setErr(null); setMsg(null);
    try {
      const r = await reportsApi.pullNow(client.id);
      const status = (r.report as any)?.parse_status;
      if (status && status !== "parsed") {
        setErr("The report was reached but could not be read. Open the Items tab for details, or try again in a few minutes.");
      } else {
        setMsg(r.analysis ? "Fresh report pulled and analyzed." : "Fresh report pulled.");
      }
      recordPulled(r.report?.id);
    } catch (e: any) {
      const body = e instanceof ApiError ? e.body : null;
      const m = body?.message || e?.message;
      if (body?.report?.id) {
        // The report WAS saved — only the analysis step failed. Keep it.
        setErr(`Report pulled, but the analysis didn't finish${m ? `: ${m}` : "."} Use Analyze Report below to retry.`);
        recordPulled(body.report.id);
      } else {
        setErr(m || "Pull failed. Try again in a few minutes.");
      }
    } finally { setPulling(false); }
  }

  return (
    <section className="sub-panel" aria-labelledby="report-schedule-title" data-testid="report-schedule-card" style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-end", justifyContent: "space-between" }}>
        <div style={{ minWidth: 240, flex: "1 1 280px" }}>
          <h3 id="report-schedule-title" style={{ margin: "0 0 4px" }}>Credit report pulls</h3>
          <p className="muted small" style={{ margin: 0 }}>
            Last pulled <strong>{when(client.last_report_at)}</strong>
            {interval > 0 && hasMonitoringLogin && <> · next automatic pull <strong>{when(client.next_auto_import_at)}</strong></>}
            {!hasMonitoringLogin && <> · no monitoring login saved, so pulls can't run yet</>}
          </p>
        </div>
        <label className="form-field" style={{ margin: 0, minWidth: 250 }}>
          <span>Automatic pull</span>
          <select value={interval} disabled={saving} onChange={(e) => changeInterval(Number(e.target.value))}
            data-testid="auto-import-interval">
            {INTERVALS.map((o) => <option key={o.days} value={o.days}>{o.label}</option>)}
          </select>
        </label>
        <button type="button" className="primary-button" onClick={pullNow} disabled={pulling || !hasMonitoringLogin}
          data-testid="pull-now-button" title={hasMonitoringLogin ? "Pull and analyze a fresh report now" : "Save the client's monitoring login first"}>
          {pulling ? "Pulling report… (about a minute)" : "Pull now"}
        </button>
      </div>
      {msg && <p className="muted small" role="status" style={{ margin: "10px 0 0", color: "var(--ne-success-text)" }}>{msg}</p>}
      {err && <p className="error-text" role="alert" style={{ margin: "10px 0 0" }}>{err}</p>}
    </section>
  );
}
